import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchInvoices, redeemPromoCode } from '../../services/api';
import { formatDate } from '../../utils/format';
import { PROMO_CODE_MAX_LENGTH, normalizePromoCode, type BillingInvoice } from '../../../shared/types';

/* Onglet « Facturation » des paramètres — extrait tel quel de
 * AccountSettingsPage.tsx (markup/classes identiques). */

export default function BillingTabContent({
  currentPlan,
  planLabel,
  billingLoading,
  onOpenBillingPortal,
}: {
  currentPlan: string;
  planLabel: string;
  billingLoading: boolean;
  onOpenBillingPortal: () => void;
}) {
  const { organization } = useAuth();
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white">Facturation</h2>

      {/* Current plan */}
      <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4 space-y-3">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Plan actuel</p>
          <p className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white">
            {planLabel}
            {organization?.subscriptionStatus === 'active' && (
              <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                Actif
              </span>
            )}
            {organization?.subscriptionStatus === 'past_due' && (
              <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                Paiement en retard
              </span>
            )}
          </p>
        </div>

        {organization?.currentPeriodEnd && (
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Prochaine échéance</p>
            <p className="text-sm text-slate-900 dark:text-white shader:text-white">
              {formatDate(organization.currentPeriodEnd)}
            </p>
          </div>
        )}

        {(organization?.creditBalance ?? 0) > 0 && (
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Crédits à l'unité</p>
            <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">
              {organization!.creditBalance} crédit{organization!.creditBalance > 1 ? 's' : ''} restant{organization!.creditBalance > 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Stripe portal for paid plans (free/beta/partner = comp, pas d'abonnement) */}
      {currentPlan !== 'free' && currentPlan !== 'beta' && currentPlan !== 'partner' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-400 shader:text-white/85">
            Gérez votre abonnement, consultez vos factures ou mettez à jour votre moyen de paiement via le portail Stripe.
          </p>
          <button
            onClick={onOpenBillingPortal}
            disabled={billingLoading}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 shader:border-white/10 text-slate-700 dark:text-slate-300 shader:text-white hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            {billingLoading ? 'Ouverture...' : 'Gérer l\'abonnement'}
          </button>
        </div>
      )}

      {/* Comp plans: no subscription */}
      {(currentPlan === 'beta' || currentPlan === 'partner') && (
        <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80">
          {currentPlan === 'beta'
            ? 'Accès offert — aucun abonnement actif.'
            : 'Accès Partenaire — utilisation illimitée offerte, aucun abonnement.'}
        </p>
      )}

      {/* Factures Stripe (abonnements et packs) — tous plans : un compte sans
          abonnement peut avoir acheté un pack. */}
      <InvoiceList showEmpty={currentPlan === 'free'} />

      <PromoCodeRedeemer />
    </div>
  );
}

const AMOUNT_FORMATTERS = new Map<string, Intl.NumberFormat>();
function formatAmount(cents: number, currency: string): string {
  const key = currency.toUpperCase();
  let fmt = AMOUNT_FORMATTERS.get(key);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: key });
    } catch {
      return `${(cents / 100).toFixed(2)} ${key}`;
    }
    AMOUNT_FORMATTERS.set(key, fmt);
  }
  return fmt.format(cents / 100);
}

/**
 * Factures Stripe de l'org, avec leur PDF. Les packs en ont une depuis
 * l'activation d'`invoice_creation` ; les achats plus anciens n'en ont pas.
 * `showEmpty` : afficher « Aucune facture disponible. » quand la liste est
 * vide (les plans offerts ont déjà leur propre message).
 */
function InvoiceList({ showEmpty }: { showEmpty: boolean }) {
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchInvoices()
      .then(list => { if (!cancelled) setInvoices(list); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  if (failed) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80">
        Impossible d'afficher vos factures pour le moment. Réessayez plus tard.
      </p>
    );
  }
  if (invoices === null) {
    return (
      <p className="text-sm text-slate-400 dark:text-slate-500 shader:text-white/60">
        Chargement des factures…
      </p>
    );
  }
  if (invoices.length === 0) {
    return showEmpty ? (
      <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80">
        Aucune facture disponible.
      </p>
    ) : null;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white">
        Factures
      </h3>
      <ul className="divide-y divide-slate-200 dark:divide-slate-700 shader:divide-white/10 rounded-lg border border-slate-200 dark:border-slate-700 shader:border-white/10">
        {invoices.map(inv => (
          <li key={inv.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
            <span className="text-slate-900 dark:text-white shader:text-white">
              {formatDate(inv.createdAt)}
            </span>
            <span className="text-slate-500 dark:text-slate-400 shader:text-white/70">
              {inv.number ? `N° ${inv.number}` : 'Facture'}
            </span>
            <span className="font-medium text-slate-900 dark:text-white shader:text-white">
              {formatAmount(inv.amountCents, inv.currency)}
            </span>
            {inv.status === 'paid' ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                Payée
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                À régler
              </span>
            )}
            <span className="ml-auto flex items-center gap-3">
              {inv.pdfUrl && (
                <a
                  href={inv.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-violet-600 dark:text-violet-400 shader:text-violet-300 underline underline-offset-2 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
                >
                  Télécharger le PDF
                </a>
              )}
              {inv.hostedUrl && (
                <a
                  href={inv.hostedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-500 dark:text-slate-400 shader:text-white/70 underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                >
                  Voir
                </a>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Saisie manuelle d'un code promo.
 *
 * Filet de sécurité du parcours QR : la carte de visite porte le code en clair
 * précisément pour le prospect qui la retrouve dans sa veste huit jours après
 * le salon, ou qui a créé son compte par un autre chemin. Le QR reste la voie
 * rapide, ce champ est la voie de rattrapage — même endpoint, même RPC.
 */
function PromoCodeRedeemer() {
  const { refreshProfile } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);

  const normalized = normalizePromoCode(code);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!normalized || busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      const result = await redeemPromoCode(normalized, 'manual');
      await refreshProfile();
      setCode('');
      setFeedback({
        ok: true,
        message: `${result.analysesGranted} analyse${result.analysesGranted > 1 ? 's' : ''} ajoutée${result.analysesGranted > 1 ? 's' : ''} à votre compte.`,
      });
    } catch (err) {
      setFeedback({ ok: false, message: err instanceof Error ? err.message : "L'échange a échoué." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-6 border-t border-slate-200 dark:border-slate-700 shader:border-white/10">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white mb-1">
        Code promo
      </h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/70 mb-3">
        Vous avez reçu une carte lors d'un salon ? Saisissez le code pour créditer vos analyses offertes.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value)}
          maxLength={PROMO_CODE_MAX_LENGTH}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          className="flex-1 min-w-[10rem] px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/15 bg-white dark:bg-slate-800 shader:bg-white/5 text-sm font-mono tracking-wide uppercase text-slate-900 dark:text-white shader:text-white placeholder:text-slate-400 placeholder:normal-case focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
        />
        <button
          type="submit"
          disabled={!normalized || busy}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          {busy ? 'Vérification…' : 'Appliquer'}
        </button>
      </form>

      {feedback && (
        <p
          className={`mt-2 text-xs ${
            feedback.ok
              ? 'text-emerald-600 dark:text-emerald-400 shader:text-emerald-300'
              : 'text-orange-600 dark:text-orange-400 shader:text-orange-300'
          }`}
        >
          {feedback.message}
        </p>
      )}
    </div>
  );
}
